import { NavigableModal } from '$components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EditMedicalDetailsForm, EditMedicalDetailsSchema, Input } from '$features/pets/forms/edit-medical-information/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { petShowOptions, petShowQueryKey, petUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'
import { PetsFilterParam } from '$features/pets/enums/filter-params'

export function EditMedicalDetailsModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...petShowOptions({
      path: {
        pet: Number(id),
      },
      query: {
        include_deleted: true,
        [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const { mutate: updatePet, isPending: isUpdatingPet } = useMutation({
    ...petUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: petShowQueryKey({
          path: {
            pet: Number(id),
          },
        }),
      })

      ToastHelper.success('Pet medical details updated successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to update pet medical details.'),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = async (values: EditMedicalDetailsSchema) => {
    updatePet({
      path: {
        pet: Number(id),
      },
      body: {
        microchip_number: values[Input.MicrochipNumber],
        weight: values[Input.Weight],
        vaccinated: values[Input.Vaccinated],
      },
    })
  }

  const pet = data?.data

  if (!pet) {
    return null
  }

  return (
    <EditMedicalDetailsForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingPet}
      defaultValues={{
        [Input.MicrochipNumber]: pet.microchip_number ?? undefined,
        [Input.Weight]: pet.weight ?? undefined,
        [Input.Vaccinated]: pet.vaccinated ?? undefined,
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Medical Details
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <EditMedicalDetailsForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <EditMedicalDetailsForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </EditMedicalDetailsForm>
  )
}
